import { css, cx, useTheme, FableTheme } from '@fable/theme'
import React from 'react'

import ArrowIcon from '../../../assets/icons/components/ArrowIcon'
import { BREAKPOINTS } from '../../../constants'
import { Button } from '@fable/components'
import { Color } from '@fable/legacy'

interface Props {
  type: 'next' | 'prev'
  isHidden: boolean
  compact?: boolean
  centerMode?: boolean
  onClick?: () => void
}

export const SliderControl: React.VFC<
  Props & React.HTMLAttributes<HTMLButtonElement>
> = ({ onClick, compact, isHidden, type, centerMode = false }) => {
  const theme = useTheme()
  const styles = getStyles(theme, centerMode)

  const regularStyles = cx(
    styles.regular,
    type === 'next' ? styles.next : styles.prev
  )
  const compactStyles = cx(
    styles.compact,
    type === 'next' ? styles.compactNext : styles.compactPrev
  )

  return (
    <Button
      negative
      className={cx(
        styles.control,
        compact ? compactStyles : regularStyles,
        type === 'next' && styles.reversed,
        isHidden && styles.hidden,
        'force-overflow'
      )}
      onClick={onClick}
    >
      <ArrowIcon fill={Color.blackSwan} />
    </Button>
  )
}

const getStyles = (
  { colors, mediaQueries }: FableTheme,
  centerMode: boolean
) => ({
  control: css`
    padding: 0;
    width: 60px;
    height: 60px;
    position: absolute;
    top: 50%;
    pointer-events: auto;
    transition: opacity ease 0.2s;
    max-width: 1072px;
    background-color: ${colors.whiteFang};
    border: 2px solid ${colors.whiteSmoke};
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.04);
    z-index: 2;

    &:hover {
      background-color: ${colors.whiteFang};
      border: 2px solid ${colors.whiteSmoke};
    }

    svg {
      width: 13px;
      height: 13px;
    }
  `,
  regular: css`
    top: calc(100% - 88px);

    ${mediaQueries.mobileL} {
      top: calc(50% - 30px);
    }
  `,
  prev: css`
    left: calc(50% - 73px);

    ${mediaQueries.mobileL} {
      left: ${centerMode ? '3%' : '-10px'};
    }

    ${mediaQueries.tablet} {
      left: ${centerMode && '8%'};
    }

    ${mediaQueries.desktop} {
      left: ${!centerMode && '-104px'};
    }

    ${mediaQueries.desktopXL} {
      left: ${centerMode && '20%'};
    }
  `,
  next: css`
    right: calc(50% - 73px);
    left: auto;

    ${mediaQueries.mobileL} {
      right: ${centerMode ? '3%' : '-10px'};
    }

    ${mediaQueries.tablet} {
      right: ${centerMode && '8%'};
    }

    ${mediaQueries.desktop} {
      right: ${!centerMode && '-104px'};
    }

    ${mediaQueries.desktopXL} {
      right: ${centerMode && '20%'};
    }
  `,
  compact: css`
    top: -38px;
    left: auto;
    width: 40px;
    height: 40px;

    svg {
      width: 10px;
      height: 10px;
    }
  `,
  compactNext: css`
    right: 32px;

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      right: 24px;
    }
  `,
  compactPrev: css`
    right: 80px;

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      right: 72px;
    }
  `,
  reversed: css`
    svg {
      transform: rotate(180deg);
    }
  `,
  hidden: css`
    opacity: 0.2;
    pointer-events: none;
  `,
})
